import { DocumentType } from '@synergy/models';
import { PoolDropzone, PoolLinearProgress, PoolTypography } from '@synergy/components';
import { Theme } from '@mui/material';
import { downloadUrl } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import * as ActivityActions from '../../../../../common/actions/activity-actions';
import * as ProductDetailFilesActions from './store/product-detail-files-actions';
import { ProductDetailFilesEditTitleModal } from './components/product-detail-files-edit-title-modal';
import { ProductDetailFilesTable } from './product-detail-files-table';
import { ProductFile } from '../../../../../common/models/product-file';
import {
    imageFilesSelector,
    otherFilesSelector,
    overallUploadProgressSelector,
} from './store/product-detail-files-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        margin: theme.spacing(6, theme.general.pageContentSpacing),
    },
    spacing: {
        marginBottom: theme.spacing(2),
    },
    progress: {
        marginTop: theme.spacing(2),
    },
    progressPlaceholder: {
        height: 4,
    },
    dropzone: {
        height: 160,
    },
    sectionContainer: {
        marginTop: theme.spacing(6),
    },
}));

export const ProductDetailFiles = () => {
    const dispatch = useThunkDispatch();
    const { id: productIdParam } = useParams<{ id: string }>();
    const classes = useStyles();
    const [selectedFile, setSelectedFile] = useState<ProductFile | null>(null);

    const productId = useAppSelector(state => state.product.detail.files.productId);
    const images = useAppSelector(imageFilesSelector);
    const otherFiles = useAppSelector(otherFilesSelector);
    const overallProgress = useAppSelector(overallUploadProgressSelector);

    useEffect(() => {
        if (!productId && productIdParam) {
            dispatch(ProductDetailFilesActions.fetchProductFiles(+productIdParam));
        }
    }, [dispatch, productId, productIdParam]);

    const handleEditTitleStart = (file: ProductFile) => {
        setSelectedFile(file);
    };

    const handleEditTitleComplete = (name: string) => {
        if (!selectedFile) {
            return;
        }
        dispatch(ProductDetailFilesActions.renameFile(selectedFile.id, name));
        setSelectedFile(null);
    };

    const handleSetPreviewImage = (id: number) => dispatch(ProductDetailFilesActions.setAsPreviewImage(id));

    const handleDownload = (url: string, filename: string) => {
        downloadUrl(url, filename);
    };

    const handleDelete = async (file: ProductFile) => {
        await dispatch(ProductDetailFilesActions.deleteProductFile(file));
        if (productId) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Product, productId));
        }
    };

    const handleDrop = async (acceptedFiles: File[]) => {
        const files = [...images, ...otherFiles];
        const deduplicatedFiles = acceptedFiles.filter(
            file => !files.some(productFile => productFile.title === file.name && productFile.size === file.size)
        );

        if (deduplicatedFiles.length > 0) {
            await dispatch(ProductDetailFilesActions.addProductFiles(deduplicatedFiles));
            if (productId) {
                dispatch(ActivityActions.fetchActivities(DocumentType.Product, productId));
            }
        }
    };

    return (
        <div className={classes.container}>
            <PoolTypography styleType="sectionTitle" id="app.product.detail.files.upload" />
            <PoolTypography typography="body1" id="app.product.detail.files.uploadText" className={classes.spacing} />
            <PoolDropzone onDrop={handleDrop} className={classes.dropzone} />
            {overallProgress ? (
                <PoolLinearProgress variant="determinate" value={overallProgress} className={classes.progress} />
            ) : (
                <div className={`${classes.progress} ${classes.progressPlaceholder}`} />
            )}
            {images && images.length > 0 && (
                <div className={classes.sectionContainer}>
                    <PoolTypography styleType="sectionTitle" id="app.product.detail.files.images" />
                    <ProductDetailFilesTable
                        files={images}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEditTitle={handleEditTitleStart}
                        onSetAsPreviewImage={handleSetPreviewImage}
                    />
                </div>
            )}

            {otherFiles && otherFiles.length > 0 && (
                <div className={classes.sectionContainer}>
                    <PoolTypography styleType="sectionTitle" id="app.product.detail.files.other" />
                    <ProductDetailFilesTable
                        files={otherFiles}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEditTitle={handleEditTitleStart}
                    />
                </div>
            )}
            <ProductDetailFilesEditTitleModal
                file={selectedFile}
                onClose={() => setSelectedFile(null)}
                onConfirm={handleEditTitleComplete}
            />
        </div>
    );
};
